// src/services/boletasService.js
const BOLETAS_BASE_URL = String(process.env.REACT_APP_BOLETAS_BASE_URL || "")
  .trim()
  .replace(/\/+$/, "");

const CONTROLLER_PATH = "/Controlador/controller_boletas.php";

// Periodo mensual "AAAA-MM" (remuneración) o "AAAA-MMG" (gratificación).
export const PERIODO_REGEX = /^\d{4}-(0[1-9]|1[0-2])G?$/;
const PERIODO_MENSUAL_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

// Meses en los que, además de la remuneración, se paga gratificación.
export const MESES_GRATIFICACION = ["07", "12"];

// "2026-07G" -> { base: "2026-07", gratificacion: true }
export function partesPeriodo(periodo) {
  const p = String(periodo || "");
  const gratificacion = p.endsWith("G");
  return { base: gratificacion ? p.slice(0, -1) : p, gratificacion };
}

export function tieneGratificacion(periodoBase) {
  return MESES_GRATIFICACION.includes(String(periodoBase || "").slice(5, 7));
}

// Las boletas de un mes se generan el día 1 del mes siguiente,
// así que el último periodo disponible es siempre el mes anterior.
export function ultimoPeriodoDisponible() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function toSafeHttpUrl(value) {
  try {
    const url = new URL(String(value || ""));
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}

async function pedirJson(op, periodo, signal, mensajeError) {
  if (!BOLETAS_BASE_URL) {
    throw new Error("Falta configurar REACT_APP_BOLETAS_BASE_URL en el archivo .env.");
  }
  if (!PERIODO_REGEX.test(String(periodo || ""))) {
    throw new Error("Periodo inválido. Use el formato YYYY-MM o YYYY-MMG.");
  }

  const params = new URLSearchParams({ op, periodo });
  const url = `${BOLETAS_BASE_URL}${CONTROLLER_PATH}?${params.toString()}`;

  let res;
  try {
    res = await fetch(url, { method: "GET", headers: { Accept: "application/json" }, signal });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new Error("No se pudo conectar con el servidor de boletas. Verifique su conexión.");
  }

  if (!res.ok) {
    throw new Error(`El servidor respondió con un error (HTTP ${res.status}).`);
  }

  let json;
  try {
    json = await res.json();
  } catch {
    throw new Error("La respuesta del servidor no es válida.");
  }

  if (!json?.OK) {
    throw new Error(String(json?.MENSAJE || json?.MESSAGE || json?.error || mensajeError));
  }
  return json;
}

export async function listarBoletasFirmadas(periodo, { signal } = {}) {
  const json = await pedirJson("listar_boletas_firmadas", periodo, signal, "No se pudo obtener las boletas firmadas.");

  const data = Array.isArray(json.DATA) ? json.DATA : [];
  return data.map((row) => ({
    persona: String(row?.PERSONA ?? ""),
    periodo: String(row?.PERIODO ?? periodo),
    nombreCompleto: String(row?.NOMBRE_COMPLETO ?? ""),
    fechaConfirmacion: String(row?.FECHA_CONFIRMACION ?? ""),
    pdfUrl: toSafeHttpUrl(row?.PDF_URL),
    uploadsUrl: row?.PERSONA ? urlBoletaUploads(String(row.PERSONA), String(row?.PERIODO ?? periodo)) : "",
  }));
}

// { firmadas, total } de un periodo. `total` (boletas emitidas) viene de
// op=resumen_boletas; si el backend aún no la tiene, se cuenta la lista de
// firmadas y `total` queda en null.
export async function resumenBoletas(periodo, { signal } = {}) {
  try {
    const { DATA } = await pedirJson("resumen_boletas", periodo, signal, "No se pudo obtener el resumen.");
    const total = Number(DATA?.TOTAL);
    const firmadas = Number(DATA?.FIRMADAS);
    if (Number.isFinite(total) && Number.isFinite(firmadas)) return { firmadas, total };
  } catch (err) {
    if (err?.name === "AbortError") throw err;
  }
  const rows = await listarBoletasFirmadas(periodo, { signal });
  return { firmadas: rows.length, total: null };
}

// Ruta del PDF ya generado en uploads/:
//   2026-08  -> uploads/2026/08/boleta_{PERSONA}_2026-08.pdf
//   2026-07G -> uploads/2026-07G/boleta_{PERSONA}_2026-07G.pdf
export function urlBoletaUploads(persona, periodo) {
  const m = PERIODO_MENSUAL_REGEX.test(periodo) ? periodo.split("-") : null;
  const carpeta = m ? `${m[0]}/${m[1]}` : encodeURIComponent(periodo);
  const archivo = `boleta_${persona}_${periodo}.pdf`;
  return `${BOLETAS_BASE_URL}/uploads/${carpeta}/${encodeURIComponent(archivo)}`;
}

// Genera el PDF (sello VERIFICADA, firma "RECIBÍ CONFORME"), lo guarda en uploads/ y lo devuelve.
export function urlBoletaGenerar(persona, periodo) {
  const params = new URLSearchParams({ op: "boleta_firmada_pdf", persona, periodo });
  return `${BOLETAS_BASE_URL}${CONTROLLER_PATH}?${params.toString()}`;
}

class HttpError extends Error {
  constructor(status) {
    super(`No se pudo obtener el PDF (HTTP ${status}).`);
    this.status = status;
  }
}

async function descargarPdf(url, signal) {
  let res;
  try {
    res = await fetch(url, { method: "GET", headers: { Accept: "application/pdf" }, signal });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new Error("No se pudo conectar con el servidor para obtener el PDF.");
  }

  if (!res.ok) throw new HttpError(res.status);

  const blob = await res.blob();
  const head = await blob.slice(0, 5).text();
  if (!head.startsWith("%PDF")) {
    throw new Error("El archivo recibido no es un PDF válido.");
  }
  return new Blob([blob], { type: "application/pdf" });
}

// Boleta CONFIRMADA: primero se lee de uploads/; si no existe, se genera.
// Se obtiene como Blob (no un iframe directo) porque el generador responde con
// Content-Disposition: attachment, lo que forzaría una descarga.
export async function obtenerBoletaFirmadaPdf(persona, periodo, { signal } = {}) {
  try {
    return await descargarPdf(urlBoletaUploads(persona, periodo), signal);
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    // 404 = aún no generada. Un error de red/CORS también cae al generador,
    // para no dejar al usuario sin boleta si uploads/ no envía cabeceras CORS.
    if (err instanceof HttpError && err.status !== 404) throw err;
  }
  return descargarPdf(urlBoletaGenerar(persona, periodo), signal);
}
